// @ts-nocheck
import { h, render } from "vue";

const DEFAULT_Z_INDEX = 2000;
const MSG_BOX_Z_INDEX = 3000;

function createModalRoot() {
  let root = document.getElementById("ov-modal-root");
  if (root) return root;

  root = document.createElement("div");
  root.id = "ov-modal-root";
  document.body.appendChild(root);
  return root;
}

function normalizeOptions(options) {
  if (!options) return {};
  return options;
}

function toCssSize(value) {
  if (value == null) return undefined;
  if (typeof value === "number") return `${value}px`;
  return value;
}

/** Build shell styles compatible with vue-js-modal's height/width options. */
function buildModalShellStyle(options) {
  const height = options.height;
  const constrained = Boolean(height && height !== "auto");

  const style = {
    width: toCssSize(options.width) || "min(980px, 100%)",
    maxHeight: options.maxHeight || "min(90vh, 100%)",
    // Constrained height: clip the shell and let .modal-body scroll.
    // height: 'auto': keep prior scrollable/visible behavior.
    overflow: constrained ? "hidden" : options.scrollable ? "auto" : "visible",
    display: "flex",
    flexDirection: "column",
    background: "white",
    borderRadius: "8px",
    boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
  };

  if (height) {
    style.height = height === "auto" ? "auto" : toCssSize(height);
  }

  const minWidth = toCssSize(options.minWidth);
  if (minWidth) style.minWidth = minWidth;

  return style;
}

function createMsgBoxComponent({ message, title, okTitle, cancelTitle, okOnly, onResult }) {
  return {
    name: "OvMsgBox",
    props: {
      close: {
        type: Function,
        required: true,
      },
    },
    methods: {
      finish(result) {
        onResult(result);
        this.close();
      },
    },
    render() {
      const buttons = [];

      if (!okOnly) {
        buttons.push(
          h(
            "button",
            {
              type: "button",
              class: "btn btn-secondary",
              onClick: () => this.finish(false),
            },
            cancelTitle,
          ),
        );
      }

      buttons.push(
        h(
          "button",
          {
            type: "button",
            class: "btn btn-primary",
            style: okOnly ? undefined : { marginLeft: "8px" },
            onClick: () => this.finish(true),
          },
          okTitle,
        ),
      );

      return h("div", { style: { padding: "20px" } }, [
        title ? h("h5", { class: "mb-3" }, title) : null,
        h("p", { class: "mb-4", style: { marginBottom: "1.25rem", whiteSpace: "pre-wrap" } }, message),
        h(
          "div",
          {
            class: "d-flex justify-content-end",
          },
          buttons,
        ),
      ]);
    },
  };
}

export function createModalPlugin() {
  /** @type {import('vue').App | null} */
  let app = null;

  /** @type {{ root?: HTMLElement, visible: boolean, close?: () => void }} */
  const state = { visible: false };

  function hide() {
    if (!state.visible) return;
    if (typeof state.close === "function") {
      state.close();
      return;
    }
    if (state.root) {
      render(null, state.root);
    }
    state.visible = false;
  }

  function show(ModalComponent, props = {}, options = {}, events = {}) {
    const root = createModalRoot();
    state.root = root;

    const normalizedOptions = normalizeOptions(options);
    const beforeClose = events && (events["before-close"] || events.beforeClose);

    const close = () => {
      try {
        if (typeof beforeClose === "function") beforeClose();
      } finally {
        render(null, root);
        state.visible = false;
        state.close = undefined;
      }
    };

    state.visible = true;
    state.close = close;

    const zIndex = normalizedOptions.zIndex || DEFAULT_Z_INDEX;
    const wrapper = {
      name: "OvModalWrapper",
      setup() {
        const onMaskClick = () => {
          if (normalizedOptions.clickToClose === false) return;
          close();
        };

        return () =>
          h(
            "div",
            {
              style: {
                position: "fixed",
                inset: "0",
                zIndex: String(zIndex),
              },
            },
            [
              h("div", {
                onClick: onMaskClick,
                style: {
                  position: "absolute",
                  inset: "0",
                  background: "rgba(0,0,0,0.55)",
                },
              }),
              h(
                "div",
                {
                  style: {
                    position: "absolute",
                    inset: "0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "16px",
                  },
                },
                [
                  h(
                    "div",
                    {
                      style: buildModalShellStyle(normalizedOptions),
                    },
                    [
                      h(ModalComponent, {
                        ...props,
                        close,
                        onClose: close,
                      }),
                    ],
                  ),
                ],
              ),
            ],
          );
      },
    };

    const vnode = h(wrapper);
    if (app) {
      vnode.appContext = app._context;
    }
    render(vnode, root);
  }

  function openMsgBox(message, options = {}, okOnly) {
    return new Promise((resolve) => {
      let settled = false;
      const finish = (result) => {
        if (settled) return;
        settled = true;
        resolve(result);
      };

      const MsgBox = createMsgBoxComponent({
        message: message == null ? "" : String(message),
        title: options.title || (okOnly ? undefined : "Please Confirm"),
        okTitle: options.okTitle || "OK",
        cancelTitle: options.cancelTitle || "Cancel",
        okOnly,
        onResult: finish,
      });

      show(
        MsgBox,
        {},
        {
          width: options.size === "sm" ? "360px" : options.width || "420px",
          clickToClose: false,
          zIndex: MSG_BOX_Z_INDEX,
        },
        {
          beforeClose: () => {
            // If closed without pressing a button, mirror BootstrapVue's falsey result.
            if (!settled) finish(false);
          },
        },
      );
    });
  }

  function msgBoxConfirm(message, options = {}) {
    return openMsgBox(message, options, false);
  }

  function msgBoxOk(message, options = {}) {
    return openMsgBox(message, options, true);
  }

  function patchBvModal(bvModal) {
    if (!bvModal || bvModal.__ovMsgBoxPatched) return;
    bvModal.msgBoxConfirm = msgBoxConfirm;
    bvModal.msgBoxOk = msgBoxOk;
    bvModal.__ovMsgBoxPatched = true;
  }

  return {
    install(application) {
      app = application;
      app.config.globalProperties.$modal = { show, hide, msgBoxConfirm, msgBoxOk };

      // BootstrapVue 2 MsgBox uses Vue.extend/$destroy and breaks under Vue 3
      // (black backdrop with orphaned text). Replace msgBox* while keeping show/hide.
      application.mixin({
        created() {
          try {
            patchBvModal(this.$bvModal);
          } catch (e) {
            // ignore if $bvModal is unavailable on this instance
          }
        },
      });
    },
    show,
    hide,
    msgBoxConfirm,
    msgBoxOk,
  };
}
